import { useGetClonePagesMetricsQuery } from '@carboncopyco/data';
import { SortIcon } from '@carboncopyco/icons';
import type { ClonedPageMetrics } from '@carboncopyco/types';
import {
  Container,
  DropdownFilter,
  Pill,
  type PillVariant,
  SkeletonRowLoading,
  TextBase,
  TextCaption,
} from '@carboncopyco/ui';
import { formatSeconds } from '@carboncopyco/utils';
import { type Col, FixedTable } from '../../../FixedTable';
import { TableRowWrapper } from '../../TableRowWrapper';
import { useClonePageProps } from '../ClonePageContext';
import { NoMetricsMessage } from '../OverviewTab/NoMetricsMessage';

const clicksLowThreshold = 10;
const clicksHighThreshold = 30;
const durationSecLowThreshold = 3 * 60;
const durationSecHighThreshold = 10 * 60;

const cols: Col<ClonedPageMetrics>[] = [
  {
    title: 'Pages',
    width: 756,
    maxContentWidth: 666,
    render: (page) => (
      <div className="flex flex-col">
        <TextBase
          label={page.url}
          color="text-ds-natural-600"
          ellipsis
        />
      </div>
    ),
  },
  {
    title: '# Clicks',
    width: 102,
    render: (page) => (
      <div className="flex items-center mr-6">
        <Pill
          label={`${page.clicks} ${page.clicks === 1 ? 'CLICK' : 'CLICKS'}`}
          variant={resolveColorByThreshold(
            page.clicks,
            clicksLowThreshold,
            clicksHighThreshold
          )}
        />
      </div>
    ),
  },
  {
    title: 'Cloning time',
    width: 90,
    render: (page) => (
      <div className="flex items-center">
        <Pill
          label={`${formatSeconds(page.durationInSeconds, {
            format: 'H[H] m [MIN]',
            formatForLessThanHour: 'm [MIN]',
            formatForLessThanMinute: 's [SEC]',
          })}`}
          variant={resolveColorByThreshold(
            page.durationInSeconds,
            durationSecLowThreshold,
            durationSecHighThreshold
          )}
        />
      </div>
    ),
  },
];

const resolveColorByThreshold = (
  size: number,
  lowThreshold: number,
  highThreshold: number
): PillVariant => {
  return size < lowThreshold
    ? 'green'
    : size < highThreshold
      ? 'primary'
      : 'red';
};

export const PagesTable = () => {
  const { organizationId, cloneId, workspaceId } = useClonePageProps();
  const {
    data: pageMetrics,
    isLoading,
    error,
  } = useGetClonePagesMetricsQuery({
    organizationId: organizationId,
    recordingId: String(cloneId),
    workspacesIds: [workspaceId],
  });

  return (
    <>
      {pageMetrics?.length || error || isLoading ? (
        <div>
          <div className="flex justify-between py-5 px-6 gap-3">
            <DropdownFilter
              items={[{ label: 'Highests clicks', id: 'clicks' }]}
              onSelect={() => {
                return;
              }}
              leftIcon={SortIcon}
              selected="clicks"
              placeholder="Highest clicks"
            />
            <div>
              {pageMetrics && (
                <TextCaption
                  color="text-ds-natural-500"
                  label={`${pageMetrics.length} cloned pages`}
                />
              )}
            </div>
          </div>
          <FixedTable<ClonedPageMetrics>
            cols={cols}
            data={pageMetrics ?? []}
            isLoading={isLoading}
            error={error}
            rowWrapper={TableRowWrapper}
            sideSpacing
            loaderPlaceholderSpace={0}
            headerRowClassName="pt-5"
            loader={
              <Container>
                <div className="w-ds-lg-modal">
                  <SkeletonRowLoading
                    medium
                    itemsCount={5}
                  />
                </div>
              </Container>
            }
          />
        </div>
      ) : (
        <div className="py-24 flex flex-col items-center gap-2">
          <NoMetricsMessage />
        </div>
      )}
    </>
  );
};
